// src/components/Footer.js
import React, { useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import newsletter from "../images/icon/newsletter.png";
import kupto from "../images/logo/kupto-final logo-2.png";
import appstore from "../images/icon/app-store.png";
import googleplay from "../images/icon/google-play.png";
import twitter from "../images/icon/twitter.png";
import facebook from "../images/icon/facebook.png";
import tiktok from "../images/icon/tiktok.png";
import pintrest from "../images/icon/pintrest.png";
import instagram from "../images/icon/instagram.png";
import "../pages/styles/footer.css";

// ✅ your brand slice exports
import { getAllBrands, selectBrands } from "../app/features/brand/brandSlice";

const Footer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const year = new Date().getFullYear();

  // ✅ brands from redux (your selector already safe)
  const brands = useSelector(selectBrands);

  // ✅ optional: read loading state if you want (safe fallback)
  const brandState = useSelector((state) => state.brand || {});
  const brandsLoading = Boolean(brandState?.isLoading);

  // ✅ login check (token or user)
  const authUser = useSelector((state) => state?.auth?.user);
  const isLoggedIn = Boolean(authUser?.token || localStorage.getItem("token"));

  // ✅ where to return after auth
  const returnTo = useMemo(
    () => (location.pathname || "/") + (location.search || ""),
    [location.pathname, location.search]
  );

  useEffect(() => {
    // ✅ fetch brands once (when empty)
    const isEmpty = !Array.isArray(brands) || brands.length === 0;
    if (isEmpty) dispatch(getAllBrands());
  }, [dispatch, brands]); // ✅ add brands to satisfy exhaustive-deps

  const shoppingLinks = useMemo(
    () => [
      { to: "/cart", label: "Your Cart" },
      { to: "/my-orders", label: "Your Orders" },
      { to: "/print", label: "Print Now" },
      { to: "/product", label: "Our Store" },
      { to: "/terms-and-conditions", label: "Terms & Conditions" },
      { to: "/privacy-policy", label: "Privacy Policy" },
      { to: "/refund-policy", label: "Refund Policy" },
    ],
    []
  );

  // ✅ UPDATED: Sign in goes to /identification (and preserve returnTo)
  const moreLinks = useMemo(
    () => [
      {
        to: isLoggedIn ? "/my-profile" : "/identification",
        label: "Sign in",
        state: !isLoggedIn ? { returnTo } : undefined,
      },
      {
        to: isLoggedIn ? "/whishlist" : "/identification",
        label: "My Wishlist",
        state: !isLoggedIn ? { returnTo } : undefined,
      },
      { to: "/help", label: "Help" },
      { to: "/contact", label: "Contact Us" },
    ],
    [isLoggedIn, returnTo]
  );

  /**
   * ✅ Normalize brand docs from backend
   * Your schema: { title: String }
   * Your API likely returns array of brand docs
   */
  const popularBrands = useMemo(() => {
    const list = Array.isArray(brands) ? brands : [];

    // accept multiple shapes just in case
    const titles = list
      .map((b) => b?.title || b?.name || b)
      .filter(Boolean)
      .map((t) => String(t).trim())
      .filter(Boolean);

    // remove duplicates
    const uniq = Array.from(new Set(titles));

    // limit for footer UI
    return uniq.slice(0, 10);
  }, [brands]);

  return (
    <footer className="kupto-footer-v2">
      {/* ================= TOP BAR ================= */}
      <div className="footer-topbar">
        <div className="footer-container footer-top-flex">
          {/* Newsletter Icon */}
          <div className="footer-newsletter-icon">
            <img src={newsletter} width={40} alt="newsletter" />
          </div>

          {/* Subscribe Title + $20 Coupon */}
          <div className="footer-subscribe-center">
            <div className="footer-subscribe-title">SUBSCRIBE NEWSLETTER</div>
            <div className="footer-subscribe-subtitle">
              …New to Kupto, get updates on our latest offers
            </div>
          </div>

          {/* Email Form */}
          <form
            className="footer-subscribe-form"
            onSubmit={(e) => e.preventDefault()}
          >
            <label className="footer-subscribe-input-wrap">
              <span className="visually-hidden">Email address</span>
              <input
                type="email"
                className="footer-subscribe-input"
                placeholder="Enter your email"
                required
              />
            </label>

            <button type="submit" className="footer-subscribe-btn">
              <span className="footer-subscribe-btn-text">Subscribe</span>
              <span className="footer-subscribe-btn-arrow">→</span>
            </button>
          </form>
        </div>
      </div>

      {/* ================= MAIN GRID ================= */}
      <div className="footer-main">
        <div className="footer-container footer-grid">
          {/* Column 1 */}
          <div className="footer-col">
            <img src={kupto} alt="Kupto logo" className="footer-logo" />
            <p className="footer-desc">
              Kupto is your online printing, branding & stationery partner –
              delivering high-quality custom products for businesses, schools and
              individuals.
            </p>
            <Link to="/about" className="footer-readmore">
              Read More →
            </Link>

            <div className="footer-install-wrapper">
              <h4 className="footer-install-title">Install App</h4>
              <p className="footer-install-text">
                From App Store or Google Play
              </p>

              <div className="footer-install-icons">
                <a
                  href="https://play.google.com/store/apps/details?id=co.kupto.printapp&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={appstore} alt="Download on App Store" />
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=co.kupto.printapp&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={googleplay} alt="Get it on Google Play" />
                </a>
              </div>
            </div>
          </div>

          {/* Column 2 */}
          <div className="footer-col">
            <h4 className="footer-title">SHOPPING</h4>
            {shoppingLinks.map((item) => (
              <Link key={item.to} to={item.to}>
                {item.label}
              </Link>
            ))}
          </div>

          {/* Column 3 */}
          <div className="footer-col">
            <h4 className="footer-title">MORE LINKS</h4>
            {moreLinks.map((item) => (
              <Link key={item.to} to={item.to} state={item.state}>
                {item.label}
              </Link>
            ))}
          </div>

          {/* Column 4 */}
          <div className="footer-col">
            <h4 className="footer-title">POPULAR BRANDS</h4>

            <div className="footer-brand-tags">
              {brandsLoading ? (
                <span className="footer-brand-tag">Loading…</span>
              ) : popularBrands.length ? (
                <>
                  {popularBrands.map((brand, index) => (
                    <Link
                      key={`${brand}-${index}`}
                      to={`/product?brand=${encodeURIComponent(brand)}`}
                      className="footer-brand-tag"
                      style={{ textDecoration: "none" }}
                    >
                      {brand}
                    </Link>
                  ))}

                  {/* optional view all */}
                  <Link
                    to="/brand"
                    className="footer-brand-tag"
                    style={{ textDecoration: "none" }}
                  >
                    View All
                  </Link>
                </>
              ) : (
                <span className="footer-brand-tag">No brands yet</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ================= BOTTOM BAR ================= */}
      <div className="footer-bottom">
        <div className="footer-container bottom-flex">
          <p>© {year} Kupto. All Rights Reserved.</p>

          <div className="footer-links-inline">
            <div className="social-icons">
              <a
                className="s-icon"
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={twitter} width={25} alt="Twitter" />
              </a>
              <a
                className="s-icon"
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={facebook} width={25} alt="Facebook" />
              </a>
              <a
                className="s-icon"
                href="https://tiktok.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={tiktok} width={25} alt="TikTok" />
              </a>
              <a
                className="s-icon"
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={instagram} width={25} alt="Instagram" />
              </a>
              <a
                className="s-icon"
                href="https://pinterest.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={pintrest} width={25} alt="Pinterest" />
              </a>
            </div>
          </div>

          <div className="footer-lang">
            <span>ENG</span> | <span>UGX</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;